import { useState } from 'react';
import type { WeeklyPlan, PlanFeedback, PlanFeedbackSummary, User } from '../types';

interface Props {
  plan: WeeklyPlan | null;
  generating: boolean;
  onGenerate: () => void;
  onShare: () => void;
  onPublish: () => void;
  feedback?: PlanFeedback[];
  feedbackSummary?: PlanFeedbackSummary | null;
  members?: User[];
  onEditAssignment?: (eventId: string, data: { pickupDriverId?: string | null; returnDriverId?: string | null }) => Promise<void>;
}

export default function WeeklyPlanPanel({ plan, generating, onGenerate, onShare, onPublish, feedback, feedbackSummary, members, onEditAssignment }: Props) {
  const [editingField, setEditingField] = useState<{ eventId: string; field: 'pickup' | 'return' } | null>(null);

  const canEdit = plan && (plan.status === 'draft' || plan.status === 'pending_review') && onEditAssignment && members && members.length > 0;

  const handleDriverChange = async (eventId: string, field: 'pickup' | 'return', value: string) => {
    if (!onEditAssignment) return;
    const driverId = value || null;
    if (field === 'pickup') {
      await onEditAssignment(eventId, { pickupDriverId: driverId });
    } else {
      await onEditAssignment(eventId, { returnDriverId: driverId });
    }
    setEditingField(null);
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Smart Driver Plan</h3>
        {plan?.status === 'published' && (
          <span className="text-[10px] font-bold uppercase bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
            Published
          </span>
        )}
        {plan?.status === 'pending_review' && (
          <span className="text-[10px] font-bold uppercase bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">
            In Review
          </span>
        )}
        {plan?.status === 'draft' && (
          <span className="text-[10px] font-bold uppercase bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full">
            Draft
          </span>
        )}
      </div>

      {!plan && (
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
            Generate a driving plan based on availability and fairness.
          </p>
          <button
            onClick={onGenerate}
            disabled={generating}
            className="w-full bg-practice hover:bg-practice/90 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 min-h-[44px]"
          >
            {generating ? 'Generating...' : 'Generate Plan'}
          </button>
        </div>
      )}

      {plan && (
        <div>
          {plan.warnings.length > 0 && (
            <div className="mb-3 space-y-1">
              {plan.warnings.map((w, i) => (
                <div key={i} className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 px-2 py-1 rounded">
                  ⚠ {w.date}: {w.message}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 mb-3">
            {plan.assignments.map(a => {
              const isEditingPickup = editingField?.eventId === a.eventId && editingField.field === 'pickup';
              const isEditingReturn = editingField?.eventId === a.eventId && editingField.field === 'return';
              const showReturnSeparately = a.returnDriverId && a.returnDriverId !== a.pickupDriverId;

              return (
                <div key={a.eventId} className="text-xs bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                      {new Date(a.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
                    </span>
                    <div className="flex-1 min-w-0 text-right">
                      {a.pickupDriverId ? (
                        <span className="text-gray-700 dark:text-gray-300">
                          {canEdit && !isEditingPickup ? (
                            <button
                              onClick={() => setEditingField({ eventId: a.eventId, field: 'pickup' })}
                              className="font-semibold underline decoration-dotted underline-offset-2 hover:text-practice transition-colors"
                            >
                              {a.pickupDriverName || 'Assigned'}
                            </button>
                          ) : !isEditingPickup ? (
                            <span className="font-semibold">{a.pickupDriverName || 'Assigned'}</span>
                          ) : null}
                          {isEditingPickup && (
                            <select
                              autoFocus
                              value={a.pickupDriverId || ''}
                              onChange={e => handleDriverChange(a.eventId, 'pickup', e.target.value)}
                              onBlur={() => setEditingField(null)}
                              className="text-xs font-semibold bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-1 text-gray-900 dark:text-white min-h-[32px]"
                            >
                              <option value="">None</option>
                              {members?.map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                          )}
                          {!showReturnSeparately && !isEditingPickup && canEdit && (
                            <button
                              onClick={() => setEditingField({ eventId: a.eventId, field: 'return' })}
                              className="text-gray-400 ml-1 hover:text-practice text-[10px]"
                              title="Set different return driver"
                            >
                              +return
                            </button>
                          )}
                          {isEditingReturn && !showReturnSeparately && (
                            <span className="ml-1">
                              <select
                                autoFocus
                                value={a.returnDriverId || ''}
                                onChange={e => handleDriverChange(a.eventId, 'return', e.target.value)}
                                onBlur={() => setEditingField(null)}
                                className="text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-1 text-gray-900 dark:text-white min-h-[32px]"
                              >
                                <option value="">Same as pickup</option>
                                {members?.map(m => (
                                  <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                              </select>
                            </span>
                          )}
                        </span>
                      ) : canEdit ? (
                        <button
                          onClick={() => setEditingField({ eventId: a.eventId, field: 'pickup' })}
                          className="text-amber-500 font-semibold underline decoration-dotted underline-offset-2 hover:text-practice"
                        >
                          Needs driver
                        </button>
                      ) : (
                        <span className="text-amber-500 font-semibold">Needs driver</span>
                      )}
                      {isEditingPickup && !a.pickupDriverId && (
                        <select
                          autoFocus
                          value=""
                          onChange={e => handleDriverChange(a.eventId, 'pickup', e.target.value)}
                          onBlur={() => setEditingField(null)}
                          className="text-xs font-semibold bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-1 text-gray-900 dark:text-white min-h-[32px] ml-1"
                        >
                          <option value="">Select driver...</option>
                          {members?.map(m => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  {/* Separate return driver row */}
                  {showReturnSeparately && (
                    <div className="flex items-center justify-between mt-1 pt-1 border-t border-gray-100 dark:border-gray-700/50">
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">Return</span>
                      <div>
                        {canEdit && !isEditingReturn ? (
                          <button
                            onClick={() => setEditingField({ eventId: a.eventId, field: 'return' })}
                            className="text-gray-400 underline decoration-dotted underline-offset-2 hover:text-practice transition-colors"
                          >
                            {a.returnDriverName || 'Assigned'}
                          </button>
                        ) : isEditingReturn ? (
                          <select
                            autoFocus
                            value={a.returnDriverId || ''}
                            onChange={e => handleDriverChange(a.eventId, 'return', e.target.value)}
                            onBlur={() => setEditingField(null)}
                            className="text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-1 text-gray-900 dark:text-white min-h-[32px]"
                          >
                            <option value="">Same as pickup</option>
                            {members?.map(m => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-gray-400">{a.returnDriverName || 'Assigned'}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Feedback summary (admin only, shown when in review or published) */}
          {feedbackSummary && (plan.status === 'pending_review' || plan.status === 'published') && (
            <div className="mb-3 border border-blue-100 dark:border-blue-900/30 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Parent Feedback</p>
              <div className="flex gap-3 mb-2">
                <span className="text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-semibold">
                  {feedbackSummary.approved} Looks Good
                </span>
                <span className="text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-2 py-0.5 rounded-full font-semibold">
                  {feedbackSummary.concerns} Concerns
                </span>
                <span className="text-xs text-gray-400">
                  {feedbackSummary.responded}/{feedbackSummary.totalMembers} responded
                </span>
              </div>
              {feedback && feedback.length > 0 && (
                <div className="space-y-1.5">
                  {feedback.map(f => (
                    <div key={f.userId} className="text-xs flex items-start gap-2">
                      <span className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${
                        f.status === 'approved'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      }`}>
                        {f.status === 'approved' ? '\u2713' : '!'}
                      </span>
                      <div className="min-w-0">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{f.userName}</span>
                        {f.comment && (
                          <p className="text-gray-500 dark:text-gray-400 mt-0.5">{f.comment}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={onGenerate}
              disabled={generating}
              className="flex-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-50 min-h-[44px]"
            >
              {generating ? 'Regenerating...' : 'Regenerate'}
            </button>
            {plan.status === 'draft' && (
              <button
                onClick={onShare}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-2 rounded-lg transition-colors min-h-[44px]"
              >
                Share for Review
              </button>
            )}
            {(plan.status === 'draft' || plan.status === 'pending_review') && (
              <button
                onClick={onPublish}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-2 rounded-lg transition-colors min-h-[44px]"
              >
                Publish
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
